import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Checkbox} from 'primereact/checkbox'
import {Message} from 'primereact/message'
import {useRef} from 'react'
import SignatureCanvas from 'react-signature-canvas'
import {HTTP_STATUS_OK} from '../../Constants'
import {tacAcceptance} from '../../api/ServiceWorker'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {openPage} from '../../utils/NavigatorUtils'
import {
    clearStorage,
    getUserId,
    setTacAcceptance,
} from '../authentication/AuthUtils'
import {uploadImage} from '../image/ImageEndPoint'
import PageContentWrapper from '../layout/PageContentWrapper'
import {showLoading, stopLoading} from '../loading/LoadingService'
import {showNotification} from '../notification/NotificationService'
import TermsDoc from './TermsDoc'
import './style/style.css'
// TODO translation the whole component
const TermsAndCondition = () => {
    useSignals()
    const active = useSignal(false)
    const acceptTerms = useSignal(false)
    const readAll = useSignal(false)
    const confirmSignature = useSignal(false)
    const contentRef = useRef()
    const blockScrollRef = useRef()
    const signatureRef = useRef(null)
    const handleMouseEnter = () => {
        blockOverflownScroll()
    }
    const {translateTerm} = TranslateUtils()
    const handleMouseLeave = () => {
        unblockOverflownScroll()
    }

    const handleScroll = () => {
        if (contentRef.current) {
            const {scrollTop, clientHeight, scrollHeight} = contentRef.current
            if (scrollTop + clientHeight >= scrollHeight - 1) {
                readAll.value = true
            }
        }
    }
    const completeSignature = async () => {
        const isEmptySignature = signatureRef.current
            .getSignaturePad()
            .isEmpty()

        if (isEmptySignature) {
            showNotification('warn', 'Warning', 'Your signature is required')

            return
        }

        let imageUrl = ''

        showLoading('Processing your form')
        const signatureBase64 = signatureRef.current
            .getTrimmedCanvas()
            .toDataURL('image/png')
        if (signatureBase64) {
            let signature = ''

            if (signatureBase64.includes('data:image/png;base64,')) {
                signature = signatureBase64.replace(
                    'data:image/png;base64,',
                    '',
                )
            }

            if (signature) {
                var formData = new FormData()
                formData.append('image', signature)

                const imageResponse = await uploadImage(formData)
                if (
                    imageResponse &&
                    !!imageResponse.success &&
                    imageResponse.data
                ) {
                    imageUrl = imageResponse.data.display_url

                    return imageUrl
                }
            }
        }

        stopLoading()
    }

    const handleAcceptTac = async () => {
        const imageUrl = await completeSignature()
        const toPostData = {
            createdAt: new Date(),
            imageUrl: imageUrl,
        }
        const acceptStatus = await tacAcceptance(getUserId(), toPostData)

        if (acceptStatus && acceptStatus === HTTP_STATUS_OK) {
            setTacAcceptance(true)
            openPage('/')
        }
    }

    const handleRejectTac = () => {
        clearStorage()
        openPage('/')
    }

    const handleOnReadAndAccept = checked => {
        acceptTerms.value = checked

        if (checked) {
            if (signatureRef.current) {
                signatureRef.current.on()
            }
        } else {
            if (signatureRef.current) {
                signatureRef.current.clear()
                signatureRef.current.off()
            }
        }
    }

    const handleOnConfirmSignature = () => {
        confirmSignature.value = true

        if (signatureRef.current) {
            signatureRef.current.off()
        }

        if (acceptTerms.value) {
            active.value = true
        } else {
            active.value = false
        }
    }

    const handleOnClearSignature = () => {
        confirmSignature.value = false

        if (signatureRef.current) {
            signatureRef.current.clear()
            signatureRef.current.on()
        }
        active.value = false
    }

    return (
        <PageContentWrapper className="flex justify-content-center h-full">
            <div
                className="col-12  md:col-10 lg:col-9 xl:col-8"
                ref={blockScrollRef}>
                <h1 className="font-bold mb-2" style={{color: 'darkcyan'}}>
                    {translateTerm('TaVIE Terms of Use')}
                </h1>
                <p>{translateTerm('Updated at ')}24-12-2024</p>
                <hr />
                <div className="relative">
                    <div
                        ref={contentRef}
                        className="bg-gray-50 border-round-lg p-3 col-12 overflow-y-auto terms-container "
                        onScroll={handleScroll}
                        style={{
                            height: '80vh',
                            scrollbarWidth: 'thin',
                        }}>
                        <TermsDoc />
                        <div className="absolute right-0 bottom-0 py-2 px-3">
                            {readAll.value ? (
                                <Message
                                    className="font-bold"
                                    severity="success"
                                    text=" Have read all the terms"
                                />
                            ) : (
                                <Message
                                    className="font-bold"
                                    severity="warn"
                                    text="You need to read all the terms to proceed to the next step."
                                />
                            )}{' '}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2 py-3 align-items-center">
                    {' '}
                    <Checkbox
                        checked={acceptTerms.value}
                        disabled={!readAll.value}
                        onChange={e => handleOnReadAndAccept(e.checked)}
                    />{' '}
                    <label className="text-xl">
                        {' '}
                        <strong>
                            You confirm that you have thoroughly read and fully
                            understood all the terms and conditions.
                        </strong>
                    </label>
                </div>
                <hr />
                <div className="flex align-items-center justify-content-between mb-3">
                    <div className="mr-2">
                        <h3 className="font-bold mb-0">
                            {translateTerm('Your signature')}
                        </h3>
                        <p className="text-lg">
                            After ensuring that you have
                            <Strong> read all the terms</Strong>, please sign
                            and click the <Strong>'Confirm signature'</Strong>{' '}
                            button on the right to complete.
                        </p>
                    </div>
                </div>
                <div className="col-12 px-0 relative">
                    {!acceptTerms.value && (
                        <div className="w-full h-full absolute"></div>
                    )}

                    <SignatureCanvas
                        ref={signatureRef}
                        penColor="white"
                        backgroundColor="darkcyan"
                        canvasProps={{
                            className: 'col-12',
                            style: {
                                // width: 800,
                                borderRadius: '10px',
                                height: 300,
                                className: 'sigCanvas',
                            },
                        }}
                        clearOnResize={false}
                    />
                </div>
                <div className="flex gap-2 px-2 justify-content-end ">
                    <Button
                        icon="pi pi-times"
                        className=" p-button-outlined h-3rem"
                        label="Clear"
                        onClick={() => handleOnClearSignature()}
                        disabled={!acceptTerms.value}
                    />
                    <Button
                        icon="pi pi-check-circle"
                        label="Confirm signature"
                        onClick={() => handleOnConfirmSignature()}
                        disabled={!acceptTerms.value}
                    />
                </div>
                <hr />
                <div className="grid mb-7">
                    <div className="col-6">
                        <Button
                            className="p-button-text col-12 p-button-lg bg-gray-50"
                            icon="pi pi-times"
                            label="CANCEL"
                            onClick={() => handleRejectTac()}
                        />
                    </div>
                    <div className="col-6">
                        <Button
                            label="ACCEPT"
                            className="col-12 p-button-lg"
                            icon="pi pi-check-circle"
                            onClick={() => handleAcceptTac()}
                            disabled={!active.value}
                        />
                    </div>
                </div>
            </div>
        </PageContentWrapper>
    )
}

const Strong = ({children}) => (
    <strong className="text-darkcyan">{children}</strong>
)

export default TermsAndCondition
